import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden.
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position.
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen.
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded document scroll position.
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Timeout used by the sticky header hiding animation.
   * @type {number | null}
   */
  #timeout = null;

  /**
   * Sticky header animation delay.
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * The announcement bar container.
   * @type {HTMLElement | null}
   */
  #announcementBar = null;

  /**
   * Timeout used to reveal the announcement bar after scrolling stops.
   * @type {number | null}
   */
  #announcementRevealTimeout = null;

  /**
   * Time to wait before revealing the announcement bar after scrolling stops.
   * @constant {number}
   */
  #announcementRevealDelay = 1000;

  /**
   * Timeout used to unlock scroll handling after the announcement transition.
   * @type {number | null}
   */
  #announcementTransitionTimeout = null;

  /**
   * Whether the announcement bar is currently transitioning into view.
   * @type {boolean}
   */
  #announcementTransitioning = false;

  /**
   * Announcement transition duration plus a small safety buffer.
   * @constant {number}
   */
  #announcementTransitionDuration = 250;

  /**
   * Mobile breakpoint used by the announcement bar behavior.
   * @type {MediaQueryList}
   */
  #mobileMediaQuery = window.matchMedia('(max-width: 749px)');

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can consume.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling to track when it is actively sticky.
   * @param {boolean} alwaysSticky
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu.
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer.
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * Clears the pending announcement reveal timeout.
   */
  #clearAnnouncementRevealTimeout() {
    if (this.#announcementRevealTimeout === null) return;

    clearTimeout(this.#announcementRevealTimeout);
    this.#announcementRevealTimeout = null;
  }

  /**
   * Prevents layout-generated scroll events from immediately hiding the bar.
   */
  #lockAnnouncementTransition() {
    if (this.#announcementTransitionTimeout !== null) {
      clearTimeout(this.#announcementTransitionTimeout);
    }

    this.#announcementTransitioning = true;

    this.#announcementTransitionTimeout = window.setTimeout(() => {
      this.#announcementTransitioning = false;
      this.#announcementTransitionTimeout = null;
    }, this.#announcementTransitionDuration);
  }

  /**
   * Updates the announcement bar visibility state.
   * @param {boolean} hidden
   */
  #setAnnouncementHidden(hidden) {
    const wasHidden = this.hasAttribute('data-announcement-hidden');

    if (wasHidden === hidden) return;

    this.toggleAttribute('data-announcement-hidden', hidden);

    if (this.#mobileMediaQuery.matches) {
      this.#lockAnnouncementTransition();
    }
  }

  /**
   * Handles announcement bar visibility while scrolling on mobile.
   * @param {number} scrollTop
   * @param {boolean} isScrollingUp
   */
  #handleAnnouncementScroll(scrollTop, isScrollingUp) {
    if (!this.#announcementBar) return;

    if (!this.#mobileMediaQuery.matches) {
      this.#clearAnnouncementRevealTimeout();
      this.#setAnnouncementHidden(false);
      return;
    }

    if (this.#announcementTransitioning) return;

    if (scrollTop <= 0 || isScrollingUp) {
      this.#clearAnnouncementRevealTimeout();
      this.#setAnnouncementHidden(false);
      return;
    }

    this.#setAnnouncementHidden(true);
    this.#clearAnnouncementRevealTimeout();

    this.#announcementRevealTimeout = window.setTimeout(() => {
      this.#setAnnouncementHidden(false);
      this.#announcementRevealTimeout = null;
    }, this.#announcementRevealDelay);
  }

  /**
   * Ensures the announcement bar is visible when leaving mobile.
   * @param {MediaQueryListEvent} event
   */
  #handleMobileBreakpointChange = (event) => {
    if (event.matches) return;

    this.#clearAnnouncementRevealTimeout();
    this.#setAnnouncementHidden(false);
  };

  /**
   * Handles header and announcement-bar behavior while scrolling.
   */
  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    this.#handleAnnouncementScroll(scrollTop, isScrollingUp);

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.setAttribute('data-animating', '');

      this.#timeout = window.setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    this.#announcementBar = this.querySelector('[data-announcement-bar-container]');

    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#mobileMediaQuery.addEventListener('change', this.#handleMobileBreakpointChange);

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#mobileMediaQuery.removeEventListener('change', this.#handleMobileBreakpointChange);
    document.removeEventListener('scroll', this.#handleWindowScroll);

    this.#clearAnnouncementRevealTimeout();

    if (this.#announcementTransitionTimeout !== null) {
      clearTimeout(this.#announcementTransitionTimeout);
      this.#announcementTransitionTimeout = null;
    }

    this.#announcementTransitioning = false;

    if (this.#timeout !== null) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    this.#announcementBar = null;
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    const children = headerGroup.children;

    for (let i = 0; i < children.length; i++) {
      const element = children[i];

      if (element === header || !(element instanceof HTMLElement)) continue;

      resizeObserver.observe(element);
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;

        const children = headerGroup.children;

        for (let i = 0; i < children.length; i++) {
          const element = children[i];

          if (element === header || !(element instanceof HTMLElement)) continue;

          resizeObserver.observe(element);
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});